import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { trackView } from "@/lib/carViews";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PaymentNotice } from "@/components/PaymentNotice";
import { Loader2, Camera, Check, Wallet, CreditCard, ShieldAlert, ArrowLeft, ArrowRight } from "lucide-react";

const DOCS = [
  { key: "license_front", label: "Patente — Fronte", required: true },
  { key: "license_back", label: "Patente — Retro", required: false },
  { key: "id_front", label: "Carta d'Identità — Fronte", required: true },
  { key: "id_back", label: "Carta d'Identità — Retro", required: false },
];

function UploadTile({ bookingId, doc, onDone }) {
  const inputRef = useRef();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    const fd = new FormData();
    fd.append("doc_type", doc.key);
    fd.append("file", file);
    try {
      await api.post(`/bookings/${bookingId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      onDone(doc.key);
      toast.success(`${doc.label} caricato`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#12141A] p-4" data-testid={`upload-${doc.key}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold">{doc.label}{doc.required && <span className="text-[#E63946]"> *</span>}</span>
        {done && <Check className="w-4 h-4 text-[#10B981]" />}
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-full aspect-[16/10] rounded-lg border-2 border-dashed border-white/15 hover:border-[#E63946]/60 transition-colors flex items-center justify-center overflow-hidden relative bg-black/30"
        data-testid={`upload-btn-${doc.key}`}
      >
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Camera className="w-7 h-7" />
            <span className="text-xs">Scatta o carica foto</span>
          </div>
        )}
        {busy && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-white" /></div>}
      </button>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} />
    </div>
  );
}

export default function BookingWizard() {
  const { carId } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [step, setStep] = useState(1);
  const [booking, setBooking] = useState(null);
  const [uploaded, setUploaded] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    pickup_date: "", return_date: "",
    pickup_location: "Pontedera", return_location: "Pontedera",
    first_name: "", last_name: "", email: "", phone: "",
    payment_method: "pay_at_pickup",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const onlineOff = !!cfg && !cfg.online_payments;
  useEffect(() => { trackView("rent", carId); api.get(`/cars/${carId}`).then((r) => setCar(r.data)).catch(() => navigate("/cars")); }, [carId]);

  const days = (() => {
    if (!form.pickup_date || !form.return_date) return 0;
    const d = (new Date(form.return_date) - new Date(form.pickup_date)) / 86400000;
    return Math.max(Math.floor(d), 1);
  })();
  const total = car ? days * car.daily_rate : 0;

  const step1Valid = form.pickup_date && form.return_date && new Date(form.return_date) > new Date(form.pickup_date)
    && form.first_name && form.last_name && form.email && form.phone;

  const createBooking = async () => {
    if (!step1Valid) { toast.error("Completa tutti i campi e verifica le date."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        car_id: carId,
        pickup_date: form.pickup_date, return_date: form.return_date,
        pickup_location: form.pickup_location, return_location: form.return_location,
        customer: { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone },
        payment_method: onlineOff ? "pay_at_pickup" : form.payment_method,
      });
      if (onlineOff) set("payment_method", "pay_at_pickup");
      setBooking(data);
      setStep(2);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const requiredDocsDone = ["license_front", "id_front"].every((d) => uploaded.includes(d));

  const finish = async () => {
    if (!requiredDocsDone) { toast.error("Carica almeno fronte patente e fronte carta d'identità."); return; }
    if (form.payment_method === "pay_online") {
      setSubmitting(true);
      try {
        const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
        setSubmitting(false);
      }
    } else {
      try { await api.post(`/bookings/${booking.id}/confirm`); } catch (e) { /* non blocca la conferma */ }
      navigate(`/booking/${booking.code}`);
    }
  };

  if (!car) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E63946]" /></div>;

  return (
    <div className="min-h-screen bg-background grain" data-testid="booking-wizard">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 pt-28 pb-20 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {/* Stepper */}
          <div className="flex items-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? "bg-[#E63946]" : "bg-white/10"}`} />
            ))}
          </div>

          {step === 1 && (
            <div className="fade-up space-y-6">
              <h2 className="font-display text-2xl font-bold uppercase">1. Dettagli noleggio</h2>
              <div className="rounded-xl border border-[#FF9E00]/30 bg-[#FF9E00]/5 p-4 flex gap-3 text-xs text-muted-foreground" data-testid="booking-conditions-note">
                <ShieldAlert className="w-5 h-5 text-[#FF9E00] shrink-0" />
                <p>Ritiro e consegna: <b className="text-foreground">Lun–Ven 8:30–19:00</b>. Il noleggio <b className="text-foreground">non è disponibile per neopatentati</b>. Per orari diversi o weekend, chiamaci allo <b className="text-foreground">0587 484791</b>.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Data ritiro</Label><Input type="date" value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="input-pickup-date" /></div>
                <div><Label>Data riconsegna</Label><Input type="date" value={form.return_date} onChange={(e) => set("return_date", e.target.value)} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="input-return-date" /></div>
                <div><Label>Luogo ritiro</Label><Input value={form.pickup_location} onChange={(e) => set("pickup_location", e.target.value)} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="input-pickup-loc" /></div>
                <div><Label>Luogo riconsegna</Label><Input value={form.return_location} onChange={(e) => set("return_location", e.target.value)} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="input-return-loc" /></div>
              </div>
              <h3 className="font-display text-lg font-bold pt-2">I tuoi dati</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Nome</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="input-first-name" /></div>
                <div><Label>Cognome</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="input-last-name" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="input-email" /></div>
                <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#12141A] border-white/10 mt-1.5" data-testid="input-phone" /></div>
              </div>

              <h3 className="font-display text-lg font-bold pt-2">Modalità di pagamento</h3>
              <PaymentNotice cfg={cfg} className="my-4" />
              <RadioGroup value={onlineOff ? "pay_at_pickup" : form.payment_method} onValueChange={(v) => set("payment_method", v)} className="grid sm:grid-cols-2 gap-4">
                <label className={`rounded-xl border p-5 cursor-pointer transition-colors ${form.payment_method === "pay_at_pickup" ? "border-[#E63946] bg-[#E63946]/5" : "border-white/10 bg-[#12141A]"}`} data-testid="payment-choice-pickup">
                  <div className="flex items-center justify-between mb-2">
                    <Wallet className="w-6 h-6 amber" /><RadioGroupItem value="pay_at_pickup" />
                  </div>
                  <div className="font-semibold">Pagamento al ritiro</div>
                  <Badge className="my-2 bg-[#10B981]/20 text-[#10B981] border-0 text-[10px]">Flessibile</Badge>
                  <p className="text-xs text-muted-foreground">Paghi al ritiro in <b className="text-foreground">contanti o con carta (POS)</b> in sede. Nessun anticipo.</p>
                </label>
                <label className={`rounded-xl border p-5 transition-colors ${onlineOff ? "opacity-45 cursor-not-allowed border-white/10 bg-[#12141A]" : form.payment_method === "pay_online" ? "cursor-pointer border-[#E63946] bg-[#E63946]/5" : "cursor-pointer border-white/10 bg-[#12141A]"}`} data-testid="payment-choice-online">
                  <div className="flex items-center justify-between mb-2">
                    <CreditCard className="w-6 h-6 amber" /><RadioGroupItem value="pay_online" disabled={onlineOff} />
                  </div>
                  <div className="font-semibold">Pagamento online</div>
                  {onlineOff ? <Badge className="my-2 bg-white/10 text-foreground border-0 text-[10px]" data-testid="payment-online-soon">Presto disponibile</Badge>
                    : <Badge className="my-2 bg-[#3B82F6]/20 text-[#3B82F6] border-0 text-[10px]">Carta di credito</Badge>}
                  <p className="text-xs text-muted-foreground">Verrai reindirizzato alla <b className="text-foreground">pagina sicura</b> dove inserire i dati della carta.</p>
                </label>
              </RadioGroup>

              <Button onClick={createBooking} disabled={submitting} className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full px-8 h-12 font-semibold" data-testid="step1-continue">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continua <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="fade-up space-y-6">
              <h2 className="font-display text-2xl font-bold uppercase">2. Caricamento documenti</h2>
              <p className="text-sm text-muted-foreground">Scatta una foto di patente e carta d'identità con lo smartphone. Fronte obbligatorio.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {DOCS.map((d) => <UploadTile key={d.key} bookingId={booking.id} doc={d} onDone={(k) => setUploaded((u) => [...new Set([...u, k])])} />)}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-full border-white/15 bg-transparent" data-testid="step2-back"><ArrowLeft className="w-4 h-4 mr-2" />Indietro</Button>
                <Button onClick={() => requiredDocsDone ? setStep(3) : toast.error("Carica fronte patente e carta d'identità.")} className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full px-8 font-semibold" data-testid="step2-continue">Continua <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="fade-up space-y-6">
              <h2 className="font-display text-2xl font-bold uppercase">3. Riepilogo e conferma</h2>
              <div className="rounded-xl border border-[#3B82F6]/30 bg-[#3B82F6]/5 p-5 flex gap-3">
                <ShieldAlert className="w-6 h-6 text-[#3B82F6] shrink-0" />
                <div>
                  <div className="font-semibold text-sm">Deposito cauzionale: {euro(car.deposit)}</div>
                  <p className="text-xs text-muted-foreground mt-1">Al ritiro viene trattenuta una cauzione temporanea di {euro(car.deposit)} sulla tua carta come garanzia. L'importo viene restituito per intero a fine noleggio se non ci sono danni.</p>
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-[#12141A] p-5 space-y-2 text-sm">
                <Row label="Auto" value={`${car.brand} ${car.model}`} />
                <Row label="Periodo" value={`${form.pickup_date} → ${form.return_date} (${days} gg)`} />
                <Row label="Ritiro / Riconsegna" value={`${form.pickup_location} / ${form.return_location}`} />
                <Row label="Pagamento" value={form.payment_method === "pay_online" ? "Online con carta" : "Al ritiro (contanti/carta)"} />
                <div className="border-t border-white/10 my-2" />
                <div className="flex justify-between items-center"><span className="font-semibold">Totale noleggio</span><span className="font-display font-extrabold text-2xl">{euro(total)}</span></div>
              </div>
              {form.payment_method === "pay_online" && (
                <p className="text-xs text-muted-foreground flex items-center gap-2" data-testid="online-pay-note">
                  <CreditCard className="w-4 h-4 amber shrink-0" /> Cliccando "Paga ora" verrai reindirizzato alla pagina di pagamento sicura (Stripe) per inserire i dati della carta.
                </p>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-full border-white/15 bg-transparent" data-testid="step3-back"><ArrowLeft className="w-4 h-4 mr-2" />Indietro</Button>
                <Button onClick={finish} disabled={submitting} className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full px-8 font-semibold" data-testid="confirm-booking-btn">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : form.payment_method === "pay_online" ? "Paga ora" : "Conferma prenotazione"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar summary */}
        <div className="lg:sticky lg:top-28 h-fit rounded-2xl border border-white/10 bg-[#181A22] overflow-hidden">
          <img src={car.image_url} alt="" className="w-full aspect-[16/10] object-cover" />
          <div className="p-6">
            <h3 className="font-display font-bold text-xl">{car.brand} {car.model}</h3>
            <div className="text-xs text-muted-foreground mt-1">{car.category} · {car.transmission} · {car.fuel}</div>
            <div className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Tariffa</span><span>{euro(car.daily_rate)} / giorno</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Giorni</span><span>{days || "—"}</span></div>
              <div className="flex justify-between text-[#3B82F6]"><span>Cauzione</span><span>{euro(car.deposit)}</span></div>
              <div className="border-t border-white/10 pt-2 flex justify-between font-semibold"><span>Totale</span><span className="font-display text-lg">{euro(total)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4"><span className="text-muted-foreground">{label}</span><span className="text-right font-medium">{value}</span></div>
);
